// src/components/admin/AssignProperties.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ChevronLeft, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, agentsAPI } from '../../services/api';
import { getDisplayName, getDisplayAddress } from '../../utils/displayName';

const AssignProperties = () => {
	const { agentId } = useParams();
	const navigate = useNavigate();
	const [selectedProperties, setSelectedProperties] = useState([]);
	const [agent, setAgent] = useState(null);
	const [properties, setProperties] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const fetchData = async () => {
		try {
			setLoading(true);
			// Fetch agent details
			const agentData = await agentsAPI.getById(agentId);
			setAgent(agentData);

			// Fetch unassigned properties using dedicated endpoint
			const propsData = await adminAPI.getUnassignedProperties();
			// This endpoint returns array directly (not paginated)
			setProperties(Array.isArray(propsData) ? propsData : []);
		} catch (error) {
			toast.error('Failed to load data');
			console.error('Error fetching data:', error);
			setProperties([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (agentId) {
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agentId]);

	const unassignedProps = properties; // Already filtered by backend endpoint

	const togglePropertySelection = (propertyId, checked) => {
		if (checked) {
			setSelectedProperties([...selectedProperties, propertyId]);
		} else {
			setSelectedProperties(selectedProperties.filter((p) => p !== propertyId));
		}
	};

	const handleAssignClick = () => {
		if (selectedProperties.length === 0) {
			toast.error('Please select at least one bill');
			return;
		}
		setShowConfirmDialog(true);
	};

	const handleConfirm = async () => {
		try {
			setLoading(true);
			await adminAPI.assignProperties(agentId, selectedProperties);
			toast.success(`Successfully assigned ${selectedProperties.length} bills to ${getDisplayName(agent)}`);
			setShowConfirmDialog(false);
			setTimeout(() => {
				navigate('/admin/assign-bills');
			}, 1500);
		} catch (error) {
			toast.error('Failed to assign bills');
			console.error('Error assigning properties:', error);
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setShowConfirmDialog(false);
	};

	if (loading && !agent) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Loading...</p>
			</div>
		);
	}

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] border-b border-white/10'>
				<div className='flex items-center gap-4 mb-6'>
					<button
						type='button'
						onClick={(e) => {
							e.preventDefault();
							navigate('/admin/assign-bills');
						}}
						className='glass-card p-3 rounded-xl'>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<div>
						<h2 className='text-xl font-black uppercase text-white'>
							Assign to {getDisplayName(agent) || 'Agent'}
						</h2>
						<p className='text-sm text-white/60'>Select bills to assign</p>
					</div>
				</div>
			</header>

			<main className='flex-1 overflow-y-auto p-6 no-scrollbar'>
				<div className='space-y-4'>
					{unassignedProps.map((prop) => {
						const propertyId = prop.id; // Use property ID (UUID) not valuation number
						const valuationNo = prop.valuationNo || prop.billNo;
						const isSelected = selectedProperties.includes(propertyId);
						return (
							<div
								key={prop.id || prop.billNo}
								className='glass-card p-4 flex items-center gap-4'>
								<input
									type='checkbox'
									className='w-5 h-5'
									checked={isSelected}
									onChange={(e) =>
										togglePropertySelection(propertyId, e.target.checked)
									}
								/>
								<div className='flex-1'>
									<p className='font-mono font-bold text-white'>{valuationNo}</p>
									<p className='text-sm text-white/60'>
										{getDisplayAddress(prop, 'Address not provided')}
									</p>
									{(prop.totalDue != null || prop.total_due != null) && (
										<p className='text-sm font-bold text-[#FFC107]'>
											Amount payable: GHC{(Number(prop.totalDue ?? prop.total_due) || 0).toLocaleString()}
										</p>
									)}
								</div>
							</div>
						);
					})}
				</div>

				{unassignedProps.length === 0 && (
					<div className='text-center py-12'>
						<CheckCircle className='w-16 h-16 text-[#00E676] mx-auto mb-4' />
						<p className='text-lg font-bold text-white'>All bills are assigned!</p>
						<p className='text-sm text-white/60 mt-2'>
							No unassigned properties available
						</p>
					</div>
				)}
			</main>

			<footer className='p-6 border-t border-white/10'>
				<button
					type='button'
					onClick={handleAssignClick}
					disabled={selectedProperties.length === 0}
					className='w-full py-4 rounded-2xl btn-gold font-black uppercase text-sm'
					style={{ opacity: selectedProperties.length === 0 ? 0.5 : 1 }}>
					Assign {selectedProperties.length} Bills
				</button>
			</footer>

			{/* Confirmation Dialog */}
			{showConfirmDialog && (
				<div className='fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4'>
					<div className='bg-[#001E3C] rounded-2xl p-6 w-full max-w-md border border-white/10'>
						<h3 className='text-xl font-black text-white mb-4'>Confirm Assignment</h3>
						<p className='text-white/80 mb-6'>
							Are you sure you want to assign these {selectedProperties.length} bills to {getDisplayName(agent)}?
						</p>
						<div className='flex gap-4'>
							<button
								type='button'
								onClick={handleCancel}
								className='flex-1 py-3 rounded-xl glass-card text-white font-bold'>
								Cancel
							</button>
							<button
								type='button'
								onClick={handleConfirm}
								disabled={loading}
								className='flex-1 py-3 rounded-xl btn-gold font-black uppercase'>
								{loading ? 'Assigning...' : 'Confirm'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AssignProperties;
